import { useEffect, useState } from 'react';
import { ApiError, api, type PhysicianSummary, type TourSheet, type TourSummary } from '../lib/api';
import { addDays, dayLabel, isoToday } from '../lib/dates';

/**
 * The physician's timesheets (D31, changed by D35): every hospital-week
 * they worked and the hours the schedule says — read only. Hours are the
 * office's to change; if something is wrong the physician sends the
 * office a question from here, which is kept on the sheet and, when the
 * service knows the office address, also opened as an email.
 */
export function Timesheets({ physician, officeEmail }: { physician: PhysicianSummary; officeEmail: string | null }) {
  const [tours, setTours] = useState<TourSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<{ siteId: string; periodStart: string } | null>(null);
  const today = isoToday();

  const load = () => {
    api.myTours(physician.abbreviation, addDays(today, -120), addDays(today, 400)).then(setTours).catch((e: Error) => setError(e.message));
  };
  useEffect(load, [physician.abbreviation]);

  if (error) return <p className="error">Couldn't load your timesheets ({error}).</p>;
  if (!tours) return <p className="muted">Loading…</p>;

  const past = tours.filter((t) => t.periodEnd <= today);
  const upcoming = tours.filter((t) => t.periodEnd > today);

  return (
    <section className="mysheets">
      <div className="card">
        <h1>Timesheets</h1>
        <p className="muted">One per hospital per week you worked (Tuesday to Tuesday), with the hours the schedule shows. Open a finished week and confirm the hours look right. If they don't, send the office a note instead — they make the correction, you don't.</p>
      </div>
      {tours.length === 0 && <p className="muted">No weeks worked in the last four months, and none scheduled.</p>}
      {past.length > 0 && <h2 className="section">Worked</h2>}
      <TourList tours={past} physician={physician} officeEmail={officeEmail} open={open} setOpen={setOpen} onChanged={load} ended />
      {upcoming.length > 0 && <h2 className="section">Coming up</h2>}
      <TourList tours={upcoming} physician={physician} officeEmail={officeEmail} open={open} setOpen={setOpen} onChanged={load} ended={false} />
    </section>
  );
}

function TourList({ tours, physician, officeEmail, open, setOpen, onChanged, ended }: { tours: TourSummary[]; physician: PhysicianSummary; officeEmail: string | null; open: { siteId: string; periodStart: string } | null; setOpen: (o: { siteId: string; periodStart: string } | null) => void; onChanged: () => void; ended: boolean }) {
  return (
    <ul className="tours">
      {tours.map((t) => {
        const isOpen = open?.siteId === t.siteId && open.periodStart === t.periodStart;
        const asking = !!t.question && !t.questionResolvedAt;
        return (
          <li key={`${t.siteId}|${t.periodStart}`} className={`tour st-${t.status}`}>
            <button type="button" className="tourhead" onClick={() => setOpen(isOpen ? null : { siteId: t.siteId, periodStart: t.periodStart })}>
              <span className="tourname"><span className={`site site-${t.siteId}`}>{t.siteName}</span> {dayLabel(t.periodStart).replace(/^\w+, /, '')} – {dayLabel(t.periodEnd).replace(/^\w+, /, '')}</span>
              <span className="hours">{t.recordedHours ?? t.scheduledHours} h</span>
              {asking ? <span className="pill st-question">question sent</span> : !ended ? <span className="pill">scheduled</span> : (
                <>
                  <span className={`pill st-${t.status}`}>{t.status === 'scheduled' ? 'as scheduled' : t.status === 'recorded' ? 'corrected by the office' : 'approved'}</span>
                  {t.physicianApprovedAt ? <span className="pill st-approved">you confirmed</span> : <span className="pill st-todo">needs your OK</span>}
                </>
              )}
            </button>
            {isOpen && <SheetView physician={physician} officeEmail={officeEmail} siteId={t.siteId} periodStart={t.periodStart} onChanged={onChanged} ended={ended} />}
          </li>
        );
      })}
    </ul>
  );
}

function SheetView({ physician, officeEmail, siteId, periodStart, onChanged, ended }: { physician: PhysicianSummary; officeEmail: string | null; siteId: string; periodStart: string; onChanged: () => void; ended: boolean }) {
  const [sheet, setSheet] = useState<TourSheet | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [asking, setAsking] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    api.mySheet(physician.abbreviation, siteId, periodStart).then(setSheet).catch((e: Error) => setError(e.message));
  }, [physician.abbreviation, siteId, periodStart]);

  if (error) return <p className="error">{error}</p>;
  if (!sheet) return <p className="muted">Loading…</p>;

  const shown = sheet.recorded ?? sheet.scheduled;
  const total = shown.reduce((n, d) => n + d.day + d.swing + d.night, 0);
  const openQuestion = sheet.question && !sheet.questionResolvedAt ? sheet.question : null;
  const subject = `Timesheet question — ${physician.firstName} ${physician.lastName}, ${sheet.siteName}, tour of ${dayLabel(periodStart).replace(/^\w+, /, '')}`;
  const mailto = (text: string) => `mailto:${encodeURIComponent(officeEmail ?? '')}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${text}\n\n— ${physician.firstName} ${physician.lastName}\nSheet: ${sheet.siteName}, ${dayLabel(periodStart)} – ${dayLabel(sheet.periodEnd)} (${total} h shown)`)}`;

  const confirmHours = async () => {
    setBusy(true);
    setProblem(null);
    try {
      setSheet(await api.approveMyHours(physician.abbreviation, siteId, periodStart));
      onChanged();
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const text = message.trim();
    if (!text) return;
    setBusy(true);
    setProblem(null);
    try {
      const updated = await api.askAboutHours(physician.abbreviation, siteId, periodStart, text);
      setSheet(updated);
      setAsking(false);
      setMessage('');
      onChanged();
      if (officeEmail) window.location.href = mailto(text);
    } catch (e) {
      setProblem(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="confirm">
      {sheet.status === 'recorded' && <p className="small muted">Corrected by the office ({sheet.recordedBy}){sheet.note ? ` — ${sheet.note}` : ''}. Changed days are marked.</p>}
      {sheet.status === 'approved' && <p className="ok small">Approved by {sheet.approvedBy}{sheet.note ? ` — ${sheet.note}` : ''}.</p>}
      <table className="hours">
        <thead>
          <tr>
            <th>Day</th>
            <th>Day<br /><span className="muted">7a–7p</span></th>
            <th>Swing<br /><span className="muted">7p–11p</span></th>
            <th>Night<br /><span className="muted">11p–7a</span></th>
            <th>Total</th>
            <th>Explanation</th>
          </tr>
        </thead>
        <tbody>
          {shown.map((d, i) => {
            const s = sheet.scheduled[i];
            const changed = (k: 'day' | 'swing' | 'night') => s && s[k] !== d[k];
            return (
              <tr key={d.date}>
                <td className="daycell">{dayLabel(d.date)}</td>
                {(['day', 'swing', 'night'] as const).map((k) => (
                  <td key={k} className="num">{changed(k) ? <strong className="warn">{d[k] || '·'}</strong> : d[k] || <span className="muted">·</span>}</td>
                ))}
                <td className="num">{d.day + d.swing + d.night || <span className="muted">·</span>}</td>
                <td className="notes">{d.note}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="confirm-foot">
        <span><strong>{total} h</strong> total{sheet.recorded && total !== sheet.totals.scheduled && <span className="muted"> (schedule said {sheet.totals.scheduled} h)</span>}</span>
        {openQuestion && (
          <p className="notice small">Your question is with the office: “{openQuestion}”</p>
        )}
        {sheet.questionResolvedAt && !openQuestion && <p className="ok small">Your question was answered by {sheet.questionResolvedBy}.</p>}
        {sheet.physicianApprovedAt && <p className="ok small">You confirmed these hours on {dayLabel(sheet.physicianApprovedAt.slice(0, 10)).replace(/^\w+, /, '')}.{sheet.approvedAt ? '' : ' The office signs them off next.'}</p>}
        {!asking ? (
          <div className="formactions">
            {ended && !sheet.physicianApprovedAt && !openQuestion && (
              <button type="button" className="primary" disabled={busy} onClick={confirmHours}>{busy ? 'Confirming…' : 'These hours are right'}</button>
            )}
            <button type="button" onClick={() => setAsking(true)}>{openQuestion ? 'Send another note' : 'Something wrong with these hours?'}</button>
            {problem && <p className="error small">{problem}</p>}
            {!ended && <span className="muted small">Hours are filled in from the schedule; the office corrects them after the week, and then you confirm.</span>}
            {ended && openQuestion && <span className="muted small">Your question is open — the office answers it, and then you can confirm.</span>}
          </div>
        ) : (
          <div className="ask">
            <label className="field grow">
              <span>What should the office know? (which day, and what happened)</span>
              <textarea value={message} maxLength={2000} rows={3} onChange={(e) => setMessage(e.target.value)} placeholder="e.g. Thursday I stayed until 11 pm for backup — the sheet shows 7 pm." autoFocus />
            </label>
            {problem && <p className="error small">{problem}</p>}
            <div className="formactions">
              <button type="button" className="primary" disabled={busy || !message.trim()} onClick={send}>{busy ? 'Sending…' : 'Send to the office'}</button>
              <button type="button" disabled={busy} onClick={() => { setAsking(false); setMessage(''); }}>Cancel</button>
              <span className="muted small">{officeEmail ? `The office sees it on their Timesheets page, and an email to ${officeEmail} opens for you to send.` : 'The office sees it on their Timesheets page.'}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
